import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Divider, Spin, Drawer, Form, Checkbox, Typography, Button } from 'antd';
import dayjs from 'dayjs';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep } from '../../atoms';
import { useAtom } from 'jotai';
import { stepConfigAtom } from '../../atoms';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import { familyService } from 'services';
import DocumentationRequirementCard, { RequiredTag } from '../../__components/DocumentationRequirementCard';
import { combineDocumentationRequirements, FAMILY_FILE_TYPES } from 'services/family.service';
import { CardTitle } from 'components/Card/Card';
import { ChildCysRevealingLink } from '../children/__components/ChildCysRevealingLink';
import { getApplicantFirstName, getOtherAdultFirstName } from '../../utils';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import FamilyAppBlankFormDownloadButton from '../../__components/FamilyAppBlankFormDownloadButton';

export function FamilyApplicationDocumentationRequirements({ application, setStep, loading, readOnly = false }) {
  const [stepConfig] = useAtom(stepConfigAtom);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [applicationFiles, setApplicationFiles] = useState(null);
  useEffect(() => {
    if (application?.id === undefined) return;

    familyService
      .getApplicationFiles(application?.id)
      .then((data) => {
        setApplicationFiles(data);
      })
      .catch((error) => {
        console.error('Error fetching application files', error);
      });
  }, [application?.id]);

  // Load requirements
  const [sponsorDocRequirements, setSponsorDocRequirements] = useState(null);
  const [otherAdultsDocRequirements, setOtherAdultsDocRequirements] = useState(null);
  const [childrenDocRequirements, setChildrenDocRequirements] = useState(null);

  // Load applicant documentation requirements
  useEffect(() => {
    if (typeof application?.id === 'number') {
      if (typeof application?.applicant?.id !== 'number') {
        setSponsorDocRequirements([]);
        return;
      }

      familyService
        .getApplicantDocumentationRequirements(application.id, application.applicant?.id)
        .then((requirements) => {
          const _combinedRequirements = combineDocumentationRequirements(requirements);
          setSponsorDocRequirements(_combinedRequirements);
        })
        .catch((error) => {
          console.error('Failed to load spouse documentation requirements:', error);
          setSponsorDocRequirements([]);
        });
    }
  }, [application?.id, application?.applicant?.id]);

  // Load spouse documentation requirements
  useEffect(() => {
    if (typeof application?.id === 'number') {
      if (typeof application?.additionalParents?.[0]?.id !== 'number') {
        setOtherAdultsDocRequirements([]);
        return;
      }

      familyService
        .getSpouseDocumentationRequirements(application.id, application.additionalParents?.[0]?.id)
        .then((requirements) => {
          const _combinedRequirements = combineDocumentationRequirements(requirements);
          setOtherAdultsDocRequirements(_combinedRequirements);
        })
        .catch((error) => {
          console.error('Failed to load spouse documentation requirements:', error);
          setOtherAdultsDocRequirements([]);
        });
    }
  }, [application?.id, application?.additionalParents]);

  // Load children documentation requirements
  useEffect(() => {
    if (typeof application?.id === 'number') {
      if (application?.children?.length === 0) {
        setChildrenDocRequirements([]);
        return;
      }

      familyService
        .getChildDocumentationRequirements(
          application.id,
          application.children?.map((child) => child.id),
        )
        .then((requirements) => {
          const _combinedRequirements = combineDocumentationRequirements(requirements);
          setChildrenDocRequirements(_combinedRequirements);
        })
        .catch((error) => {
          console.error('Failed to load spouse documentation requirements:', error);
          setChildrenDocRequirements([]);
        });
    }
  }, [application?.children, application?.id]);

  // Check if any required documents are missing
  const missingRequiredDocuments = useMemo(() => {
    if (!sponsorDocRequirements || !otherAdultsDocRequirements || !childrenDocRequirements || !applicationFiles) {
      return [];
    }

    const allRequirements = [...sponsorDocRequirements, ...otherAdultsDocRequirements, ...childrenDocRequirements];

    return allRequirements.filter((requirement) => {
      if (requirement.optional) {
        return false;
      }

      const _matchingFiles = applicationFiles.filter((file) => file.fileType === requirement.fileType);
      return _matchingFiles.length === 0;
    });
  }, [sponsorDocRequirements, otherAdultsDocRequirements, childrenDocRequirements, applicationFiles]);

  if (
    applicationFiles === null ||
    sponsorDocRequirements === null ||
    otherAdultsDocRequirements === null ||
    childrenDocRequirements === null
  ) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Document Checklist" />

      <div className="p-7">
        <Alert
          type="info"
          message="Document Checklist"
          description="See what's needed to complete your application"
          showIcon
        />

        {sponsorDocRequirements?.length !== 0 && (
          <>
            <Banner question={`${getApplicantFirstName(application?.applicant)}'s documents`} className="mt-12 mb-8" />
            {/* Dynamic Documentation Cards */}
            {sponsorDocRequirements?.map((requirement, index) => (
              <DocumentationRequirementCard
                key={requirement.fileType}
                requirement={requirement}
                files={applicationFiles}
                setFiles={setApplicationFiles}
                application={application}
                readOnly={readOnly}
                title={<CardTitle title={`${index + 1}. ${requirement.title}`} />}
                className={index > 0 ? 'mt-10' : ''}
                showRequirementStatus
              />
            ))}

            <Divider className="my-16" />
          </>
        )}

        {otherAdultsDocRequirements?.length !== 0 && (
          <>
            <Banner
              question={`${getOtherAdultFirstName(application?.additionalParents?.[0])}'s documents`}
              className="mt-12 mb-8"
            />
            {/* Dynamic Documentation Cards */}
            {otherAdultsDocRequirements?.map((requirement, index) => (
              <DocumentationRequirementCard
                key={requirement.fileType}
                requirement={requirement}
                files={applicationFiles}
                setFiles={setApplicationFiles}
                application={application}
                readOnly={readOnly}
                title={<CardTitle title={`${index + 1}. ${requirement.title}`} />}
                className={index > 0 ? 'mt-10' : ''}
                showRequirementStatus
                uploadFileProps={
                  requirement.fileType === FAMILY_FILE_TYPES.SPOUSE_WORK?.code
                    ? {
                        uploadButtonText: 'Upload form(s)',
                        preUploadButtonContent: <FamilyAppBlankFormDownloadButton fileType={requirement.fileType} />,
                      }
                    : {}
                }
              />
            ))}

            <Divider className="my-16" />
          </>
        )}

        {childrenDocRequirements?.length !== 0 && (
          <>
            <Banner question="Children & Care" className="mt-12 mb-8" />
            {/* Dynamic Documentation Cards */}
            {childrenDocRequirements?.map((requirement, index) => {
              return (
                <DocumentationRequirementCard
                  key={requirement.fileType}
                  requirement={requirement}
                  title={<CardTitle title={`${index + 1}. ${requirement.title}`} />}
                  descriptionsFooter={
                    requirement.fileType === FAMILY_FILE_TYPES.CHILD_CYS?.code ? <ChildCysRevealingLink /> : null
                  }
                  uploadFileProps={
                    [FAMILY_FILE_TYPES.PROVIDER_RATES?.code, FAMILY_FILE_TYPES.CHILD_CYS?.code].includes(
                      requirement.fileType,
                    )
                      ? {
                          uploadButtonText: 'Upload form(s)',
                          preUploadButtonContent: (
                            <FamilyAppBlankFormDownloadButton
                              fileType={requirement.fileType}
                              branchId={application?.applicant?.militaryDetails?.branch?.id}
                            />
                          ),
                        }
                      : {}
                  }
                  files={applicationFiles}
                  setFiles={setApplicationFiles}
                  application={application}
                  readOnly={readOnly}
                  className={index > 0 ? 'mt-10' : ''}
                  showRequirementStatus
                  showPersonName
                />
              );
            })}
          </>
        )}
      </div>

      <ApplicationWizardActions
        applicationIsReadOnly={readOnly}
        backButtonProps={{
          onClick: () => {
            const previousStep = getPreviousStep(
              FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS,
              application,
              stepConfig,
            );
            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
          'data-testid': 'documentation-requirements-back-button',
        }}
        nextButtonProps={{
          text: missingRequiredDocuments?.length === 0 ? 'Next' : 'Submit without all documents',
          onClick: () => {
            if (missingRequiredDocuments?.length !== 0) {
              setDrawerOpen(true);
            } else {
              const nextStep = getNextStep(
                FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS,
                application,
                stepConfig,
              );
              setStep({
                newStep: nextStep,
                applicationId: application?.id,
              });
            }
          },
          'data-testid': 'documentation-requirements-next-button',
        }}
      />

      <DocumentationRequirementsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        application={application}
        setStep={setStep}
        stepConfig={stepConfig}
        missingRequiredDocuments={missingRequiredDocuments}
      />
    </>
  );
}

const checkboxRules = [
  {
    validator: (_, value) => {
      if (!value) {
        return Promise.reject(new Error('Please check this box to continue'));
      }

      return Promise.resolve();
    },
  },
];

function DocumentationRequirementsDrawer({
  open,
  onClose,
  application,
  setStep,
  stepConfig,
  missingRequiredDocuments,
}) {
  const [form] = Form.useForm();

  const handleContinueWithoutDocuments = async () => {
    try {
      await form.validateFields();

      // All checkboxes are checked, proceed to next step
      const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.DOCUMENTATION_REQUIREMENTS, application, stepConfig);
      setStep({
        newStep: nextStep,
        applicationId: application?.id,
      });

      onClose();
    } catch (error) {
      // Validation failed, form will show errors
      console.log('Validation failed:', error);
    }
  };

  return (
    <Drawer
      title="Some required documents are missing"
      placement="right"
      onClose={onClose}
      open={open}
      width={480}
      footer={
        <div className="flex flex-col gap-2">
          <Button type="primary" onClick={onClose}>
            Go back and upload
          </Button>

          <Button color="primary" variant="outlined" onClick={handleContinueWithoutDocuments}>
            Submit now, upload later
          </Button>
        </div>
      }
    >
      <h4 className="font-bold mb-3 text-gray-900">Check these areas:</h4>
      <div className="flex flex-col gap-3">
        {missingRequiredDocuments?.map((missingDoc) => (
          <div className="bg-[white] border border-solid border-[#e7e7e7] flex flex-col xs:flex-row justify-between gap-4 items-start xs:items-center py-4 px-6 rounded-xl shadow-[0_10px_30px_-16px_rgba(0,0,0,0.1)]">
            {missingDoc?.title}

            <RequiredTag className="mr-0" />
          </div>
        ))}
      </div>

      <Alert
        type="warning"
        description={
          <>
            <h4 className="font-bold mb-1 text-gray-900">You can still submit your application</h4>
            <Typography.Text type="secondary" className="block mb-6">
              But submitting without all documents may delay approval
            </Typography.Text>

            <Form form={form} layout="vertical">
              <div className="space-y-4">
                <Form.Item name="longerProcessing" valuePropName="checked" rules={checkboxRules}>
                  <Checkbox>
                    <div className="translate-y-[-3px]">
                      <div className="font-medium">I understand processing may take extra time</div>
                      <div className="text-sm text-gray-600">Complete applications are approved faster</div>
                    </div>
                  </Checkbox>
                </Form.Item>

                <Form.Item name="payCostsInitially" valuePropName="checked" rules={checkboxRules}>
                  <Checkbox>
                    <div className="translate-y-[-3px]">
                      <div className="font-medium">I may need to pay providers upfront initially</div>
                      <div className="text-sm text-gray-600">Fee assistance begins after approval</div>
                    </div>
                  </Checkbox>
                </Form.Item>

                <Form.Item name="deadline" valuePropName="checked" rules={checkboxRules}>
                  <Checkbox>
                    <div className="translate-y-[-3px]">
                      <div className="font-medium">
                        You can upload documents until {dayjs().add(90, 'day').format(DATE_FORMAT_SENTENCE)}
                      </div>
                      <div className="text-sm text-gray-600">We'll send reminders to help you stay on track</div>
                    </div>
                  </Checkbox>
                </Form.Item>
              </div>
            </Form>
          </>
        }
        className="mt-8"
      />
    </Drawer>
  );
}
